import dayjs, { type Dayjs } from 'dayjs';
import parsePhoneNumberFromString from 'libphonenumber-js';

import type { ILead, ICreateLeadRequest } from 'src/services/api';
import {
    type LeadForm,
    LeadFormField,
} from 'src/components/create-or-update-lead-modal/types';

export const sharedLeadFormOptions = {
    [LeadFormField.Name]: {
        required: 'Name is required',
        maxLength: {
            value: 100,
            message: 'Name must be less than 100 characters'
        },
    },
    [LeadFormField.Email]: {
        pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: 'Please use a valid email address',
        },
        validate: (value: string, values: LeadForm) => {
            if (!value && !values.phoneNumber && !values.facebookUserId) {
                return 'Email or phone number is required';
            }

            return true;
        },
        deps: [LeadFormField.PhoneNumber, LeadFormField.FacebookUserId],
    },
    [LeadFormField.PhoneNumber]: {
        validate: (value: string, values: LeadForm) => {
            if (!value && !values.email && !values.facebookUserId) {
                return 'Email or phone number is required';
            }

            if (!value || value === '+') {
                return true;
            }

            const phoneNumberParsed = parsePhoneNumberFromString(value, 'US');
            if (!phoneNumberParsed || !phoneNumberParsed.isPossible()) {
                return 'Please use a valid US phone number.';
            }

            return true;
        },
        deps: [LeadFormField.Email, LeadFormField.FacebookUserId],
    }
};

export const createLeadFormOptions = {
    ...sharedLeadFormOptions,
    ingestionSource: {
        required: 'Ingestion source is required',
    },
    firstContactDate: {
        required: 'First contact date is required',
        validate: (value: Dayjs | null) => {
            return value?.isValid ? true : 'Invalid date';
        }
    },
};

export const updateLeadFormOptions = {
    ...sharedLeadFormOptions,
    ownerId: {
        required: 'Owner is required',
    },
};

/**
 * @param phoneNumber - The phone number to normalize
 * @returns The normalized phone number with +1 country code for US numbers, or empty string if invalid
 */
export const normalizePhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) {
        return '';
    }

    let parsed = parsePhoneNumberFromString(phoneNumber, 'US');

    if (!parsed || !parsed.isValid() || parsed.country !== 'US') {
        const digitsOnly = phoneNumber.replace(/[^0-9]/g, '');

        if (digitsOnly.length === 10 || digitsOnly.length === 11) {
            if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
                // If number is 11 digits and starts with 1, add + prefix and parse
                parsed = parsePhoneNumberFromString(`+${digitsOnly}`, 'US');
            } else if (digitsOnly.length === 10) {
                // If number is 10 digits, add +1 prefix
                parsed = parsePhoneNumberFromString(`+1${digitsOnly}`, 'US');
            }
        }
    }

    if (parsed && parsed.isValid() && parsed.country === 'US') {
        return parsed.format('E.164');
    }

    const cleaned = phoneNumber.replace(/[^0-9+]/g, '');
    if (cleaned && !cleaned.startsWith('+1')) {
        const digitsOnly = cleaned.replace('+', '');
        if (digitsOnly.length === 10) {
            return `+1${digitsOnly}`;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
            return `+${digitsOnly}`;
        } else {
            return cleaned;
        }
    } else {
        return cleaned || '';
    }
};

export const getDefaultValues = (lead?: ILead): LeadForm => {
    const normalizedPhoneNumber = normalizePhoneNumber(lead?.phone_number || '');

    return {
        name: lead?.name || '',
        email: lead?.email || '',
        phoneNumber: normalizedPhoneNumber,
        ingestionSource: lead?.ingestion_source || '',
        propertyId: String(lead?.property_id),
        firstContactDate: dayjs(),
        labels: lead?.labels || [],
        ownerId: String(lead?.owner),
        facebookUserId: lead?.facebook_user_id || '',
    };
};

export const getCreateLeadPayload = (values: LeadForm, propertyName?: string): ICreateLeadRequest => {
    const phoneNumber = (!values.phoneNumber || values.phoneNumber === '+') ? null : values.phoneNumber;
    return {
        name: values.name,
        email: values.email || null,
        phone_number: phoneNumber,
        ingestion_source: values.ingestionSource,
        property: propertyName,
        labels: values.labels,
        is_synthetic: false,
    };
};

export const getUpdateLeadPayload = (lead: ILead, values: LeadForm) => {
    const phoneNumber = (!values.phoneNumber || values.phoneNumber === '+') ? null : values.phoneNumber;
    const propertyId = values.propertyId === '-1' ? -1 : (Number(values.propertyId) || null);

    return {
        uuid: lead.uuid,
        name: values.name,
        email: values.email || null,
        phone_number: phoneNumber,
        property_id: propertyId,
        owner: Number(values.ownerId),
        labels: values.labels,
    };
};
